import { useState, useRef, useCallback, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  Bluetooth,
  CheckCircle2,
  AlertCircle,
  Loader2,
  Trash2,
  Volume2,
  VolumeX,
  ScanBarcode,
  Package,
  X,
  Zap
} from 'lucide-react';
import { toast } from 'sonner';
import api from '../../../utils/api';
import { useLanguage } from '../../../contexts/LanguageContext';
import BatchConfirmationGrid from './BatchConfirmationGrid';

// Configuration
const RAPID_INPUT_THRESHOLD_MS = 50; // Max time between characters for scanner input
const SCANNER_SUBMIT_DELAY_MS = 100; // Delay after last character before processing
const MIN_BARCODE_LENGTH = 4;
const MAX_BARCODE_LENGTH = 30;

// Sound settings
const SCAN_BEEP_FREQUENCY = 2000;
const SCAN_BEEP_DURATION = 80;

/**
 * RapidScanInput - Phase 2B Component
 * 
 * Supports Bluetooth/USB barcode scanners that act as HID keyboard devices.
 * Detects rapid keystroke pattern and auto-processes barcodes.
 */
export default function RapidScanInput({ onComplete, onCancel }) {
  const { t } = useLanguage();
  
  // Refs
  const inputRef = useRef(null);
  const audioContextRef = useRef(null);
  const lastKeyTimeRef = useRef(0);
  const inputBufferRef = useRef('');
  const processTimeoutRef = useRef(null);
  
  // State
  const [isActive, setIsActive] = useState(false);
  const [scanQueue, setScanQueue] = useState([]);
  const [processing, setProcessing] = useState(false);
  const [soundEnabled, setSoundEnabled] = useState(true);
  const [showResults, setShowResults] = useState(false);
  const [processedResults, setProcessedResults] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [lastScanned, setLastScanned] = useState(null);

  // Initialize audio context
  useEffect(() => {
    return () => {
      if (audioContextRef.current) {
        audioContextRef.current.close();
      }
      if (processTimeoutRef.current) {
        clearTimeout(processTimeoutRef.current);
      }
    };
  }, []);

  // Play scan sound
  const playBeep = useCallback(() => {
    if (!soundEnabled) return;
    
    try {
      if (!audioContextRef.current) {
        audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
      }
      
      const ctx = audioContextRef.current;
      const oscillator = ctx.createOscillator();
      const gainNode = ctx.createGain();
      
      oscillator.connect(gainNode);
      gainNode.connect(ctx.destination);
      
      oscillator.frequency.value = SCAN_BEEP_FREQUENCY;
      oscillator.type = 'sine';
      
      gainNode.gain.setValueAtTime(0.25, ctx.currentTime);
      gainNode.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + SCAN_BEEP_DURATION / 1000);
      
      oscillator.start(ctx.currentTime);
      oscillator.stop(ctx.currentTime + SCAN_BEEP_DURATION / 1000);
      
    } catch (e) {
      // Fallback beep
      try {
        const beep = new Audio('data:audio/wav;base64,UklGRnoGAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQoGAACBhYqFbF1fdH2Onp+fnZuYk42HgHp0cm9xcnZ7gYiOlJmdoKKioJ2ZlI+JgoF+fn+Cg4WGh4iJiomJiIeFg4B9e3l4eHl7fYCDhomLjY+QkZGQj42Kh4R/');
        beep.volume = 0.25;
        beep.play();
      } catch (e2) {
        console.log('Audio not supported');
      }
    }
  }, [soundEnabled]);

  // Trigger haptic
  const triggerHaptic = useCallback(() => {
    if (navigator.vibrate) {
      navigator.vibrate(30);
    }
  }, []);

  // Process scanned barcode
  const processScannedCode = useCallback((code) => {
    // Validate barcode
    const trimmedCode = code.trim();
    if (trimmedCode.length < MIN_BARCODE_LENGTH || trimmedCode.length > MAX_BARCODE_LENGTH) {
      return;
    }
    
    // Check if already in queue
    if (scanQueue.some(item => item.code === trimmedCode)) {
      toast.info(`Already scanned: ${trimmedCode}`);
      return;
    }
    
    // Add to queue
    setScanQueue(prev => [...prev, {
      code: trimmedCode,
      timestamp: Date.now(),
      status: 'pending'
    }]);
    
    setLastScanned(trimmedCode);
    
    // Feedback
    playBeep();
    triggerHaptic();
    
    toast.success(`Scanned: ${trimmedCode}`, {
      duration: 1500,
      position: 'top-center'
    });
    
  }, [scanQueue, playBeep, triggerHaptic]);

  // Handle keydown
  const handleKeyDown = useCallback((e) => {
    const now = Date.now();
    const timeSinceLastKey = now - lastKeyTimeRef.current;
    
    // Clear any pending process timeout
    if (processTimeoutRef.current) {
      clearTimeout(processTimeoutRef.current);
    }
    
    // If Enter key, process the buffer
    if (e.key === 'Enter') {
      e.preventDefault();
      const code = inputBufferRef.current;
      inputBufferRef.current = '';
      lastKeyTimeRef.current = 0;
      
      if (code.length >= MIN_BARCODE_LENGTH) {
        processScannedCode(code);
      }
      return;
    }
    
    // Filter to printable characters
    if (e.key.length === 1) {
      // Detect if this is rapid scanner input
      const isRapidInput = timeSinceLastKey < RAPID_INPUT_THRESHOLD_MS || lastKeyTimeRef.current === 0;
      
      if (!isRapidInput && inputBufferRef.current.length > 0) {
        // Manual typing detected, clear buffer
        inputBufferRef.current = '';
      }
      
      inputBufferRef.current += e.key;
      lastKeyTimeRef.current = now;
      
      // Set timeout to process if no more input
      processTimeoutRef.current = setTimeout(() => {
        const code = inputBufferRef.current;
        if (code.length >= MIN_BARCODE_LENGTH) {
          processScannedCode(code);
        }
        inputBufferRef.current = '';
        lastKeyTimeRef.current = 0;
      }, SCANNER_SUBMIT_DELAY_MS);
    }
    
  }, [processScannedCode]);

  // Activate rapid scan mode
  const activateMode = useCallback(() => {
    setIsActive(true);
    inputBufferRef.current = '';
    lastKeyTimeRef.current = 0;
    
    // Focus the hidden input
    setTimeout(() => {
      inputRef.current?.focus();
    }, 100);
  }, []);

  // Deactivate
  const deactivateMode = useCallback(() => {
    setIsActive(false);
    inputBufferRef.current = '';
    if (processTimeoutRef.current) {
      clearTimeout(processTimeoutRef.current);
    }
  }, []);

  // Clear queue
  const clearQueue = useCallback(() => {
    setScanQueue([]);
    setLastScanned(null);
  }, []);

  // Remove from queue
  const removeFromQueue = useCallback((code) => {
    setScanQueue(prev => prev.filter(item => item.code !== code));
  }, []);

  // Process queue
  const processQueue = useCallback(async () => {
    if (scanQueue.length === 0) return;
    
    setProcessing(true);
    deactivateMode();
    
    try {
      const results = await Promise.all(
        scanQueue.map(async (item) => {
          try {
            const response = await api.post('/barcode/scan', {
              code: item.code,
              include_lookup: true
            });
            
            if (response.data.success) {
              return {
                raw_code: item.code,
                barcode_type: response.data.result?.format || 'Unknown',
                parsed: response.data.result,
                confidence: response.data.result?.confidence || 0.5,
                source: response.data.result?.source || 'barcode',
                ready_to_post: response.data.result?.product_name !== null,
                needs_input: !response.data.result?.product_name
              };
            } else {
              return {
                raw_code: item.code,
                barcode_type: 'Unknown',
                parsed: null,
                confidence: 0,
                ready_to_post: false,
                needs_input: true,
                error: 'Lookup failed'
              };
            }
          } catch (err) {
            return {
              raw_code: item.code,
              barcode_type: 'Unknown',
              parsed: null,
              confidence: 0,
              ready_to_post: false,
              needs_input: true,
              error: err.message
            };
          }
        })
      );
      
      setProcessedResults(results);
      setShowResults(true);
      
      const readyCount = results.filter(r => r.ready_to_post).length;
      toast.success(
        `Processed ${results.length} barcodes: ${readyCount} ready`
      );
      
    } catch (err) {
      console.error('Queue processing error:', err);
      toast.error('Failed to process barcodes');
    } finally {
      setProcessing(false);
    }
  }, [scanQueue, deactivateMode]);

  // Handle batch submit
  const handleBatchSubmit = useCallback(async (items) => {
    if (!items || items.length === 0) return;
    
    setSubmitting(true);
    
    try {
      const response = await api.post('/barcode/batch-create-rshd', { items });
      
      if (response.data.success) {
        const { created, failed } = response.data.summary;
        
        if (created > 0) {
          toast.success(`Successfully posted ${created} products!`);
        }
        if (failed > 0) {
          toast.warning(`${failed} products could not be posted`);
        }
        
        if (onComplete) {
          onComplete(response.data);
        }
        
        clearQueue();
        setShowResults(false);
        setProcessedResults([]);
      }
    } catch (err) {
      console.error('Batch submit error:', err);
      toast.error(err.response?.data?.detail || 'Failed to post products');
    } finally {
      setSubmitting(false);
    }
  }, [onComplete, clearQueue]);

  // Back to scanning
  const handleBackToScanning = useCallback(() => {
    setShowResults(false);
    setProcessedResults([]);
  }, []);

  // Results view
  if (showResults) {
    return (
      <div className="space-y-4">
        <Button variant="ghost" onClick={handleBackToScanning} className="mb-2">
          <Bluetooth className="w-4 h-4 mr-2" />
          {t('scanner_ui.back_to_rapid_scan', 'Back to Rapid Scan')}
        </Button>
        
        <BatchConfirmationGrid
          items={processedResults}
          onSubmit={handleBatchSubmit}
          onCancel={() => {
            handleBackToScanning();
            clearQueue();
          }}
          loading={submitting}
          title={t('rapid_scan.review', 'Review Scanned Products')}
          submitLabel={t('rapid_scan.post_selected', 'Post Selected')}
        />
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="rapid-scan-input">
      {/* Hidden input for capturing scanner keystrokes */}
      <input
        ref={inputRef}
        type="text"
        className="opacity-0 absolute -z-10 w-0 h-0"
        onKeyDown={handleKeyDown}
        onBlur={() => {
          // Re-focus if still active
          if (isActive) {
            setTimeout(() => inputRef.current?.focus(), 100);
          }
        }}
        autoComplete="off"
        data-testid="rapid-scan-hidden-input"
      />
      
      {/* Main Card */}
      <Card className={isActive ? 'ring-2 ring-blue-500' : ''}>
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-lg flex items-center gap-2">
                <Bluetooth className="w-5 h-5 text-blue-600" />
                {t('rapid_scan.title', 'Rapid Scanner Mode')}
              </CardTitle>
              <CardDescription>
                {t('rapid_scan.description', 'Use a Bluetooth or USB barcode scanner for high-speed scanning')}
              </CardDescription>
            </div>
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setSoundEnabled(!soundEnabled)}
            >
              {soundEnabled ? (
                <Volume2 className="w-5 h-5 text-green-600" />
              ) : (
                <VolumeX className="w-5 h-5 text-gray-400" />
              )}
            </Button>
          </div>
        </CardHeader>
        
        <CardContent>
          {/* Status Display */}
          <div className={`p-6 rounded-lg text-center mb-4 transition-colors ${
            isActive 
              ? 'bg-blue-50 border-2 border-blue-200' 
              : 'bg-gray-50 border-2 border-dashed border-gray-200'
          }`}>
            {isActive ? (
              <>
                <div className="flex justify-center mb-3">
                  <div className="relative">
                    <Zap className="w-12 h-12 text-blue-600" />
                    <span className="absolute -top-1 -right-1 flex h-3 w-3">
                      <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-blue-400 opacity-75"></span>
                      <span className="relative inline-flex rounded-full h-3 w-3 bg-blue-500"></span>
                    </span>
                  </div>
                </div>
                <h3 className="text-lg font-semibold text-blue-800 mb-1">
                  {t('rapid_scan.ready', 'Ready for Scanning')}
                </h3>
                <p className="text-sm text-blue-600">
                  {t('rapid_scan.ready_desc', 'Point your scanner and start scanning barcodes')}
                </p>
                {lastScanned && (
                  <Badge className="mt-3 bg-green-500">
                    {t('scanner_ui.last', 'Last:')} {lastScanned}
                  </Badge>
                )}
              </>
            ) : (
              <>
                <Bluetooth className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                <h3 className="text-lg font-semibold text-gray-600 mb-1">
                  {t('rapid_scan.inactive', 'Scanner Mode Inactive')}
                </h3>
                <p className="text-sm text-gray-500">
                  {t('rapid_scan.inactive_desc', 'Click below to activate rapid scanning')}
                </p>
              </>
            )}
          </div>
          
          {/* Controls */}
          <div className="flex gap-3">
            {!isActive ? (
              <Button
                onClick={activateMode}
                className="flex-1 bg-blue-600 hover:bg-blue-700"
                data-testid="activate-rapid-scan"
              >
                <Zap className="w-4 h-4 mr-2" />
                {t('rapid_scan.activate', 'Activate Scanner Mode')}
              </Button>
            ) : (
              <Button
                variant="outline"
                onClick={deactivateMode}
                className="flex-1"
                data-testid="deactivate-rapid-scan"
              >
                {t('rapid_scan.deactivate', 'Stop Scanning')}
              </Button>
            )}
            
            {scanQueue.length > 0 && (
              <Button
                onClick={processQueue}
                disabled={processing}
                className="flex-1"
                data-testid="process-rapid-queue"
              >
                {processing ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    {t('scanner_ui.processing', 'Processing...')}
                  </>
                ) : (
                  <>
                    <CheckCircle2 className="w-4 h-4 mr-2" />
                    {t('rapid_scan.done', 'Done')} ({scanQueue.length})
                  </>
                )}
              </Button>
            )}
          </div>
        </CardContent>
      </Card>
      
      {/* Scan Queue */}
      {scanQueue.length > 0 && (
        <Card>
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="text-base flex items-center gap-2">
                <Package className="w-4 h-4 text-blue-600" />
                {t('rapid_scan.queue', 'Scanned Items')}
                <Badge variant="outline">{scanQueue.length}</Badge>
              </CardTitle>
              <Button
                variant="ghost"
                size="sm"
                onClick={clearQueue}
                className="text-red-500 hover:text-red-700"
              >
                <Trash2 className="w-4 h-4 mr-1" />
                {t('scanner_ui.clear', 'Clear')}
              </Button>
            </div>
          </CardHeader>
          <CardContent className="pt-0">
            <ScrollArea className="h-[180px]">
              <div className="space-y-2">
                {scanQueue.map((item, index) => (
                  <div
                    key={item.code}
                    className="flex items-center justify-between p-2 bg-gray-50 rounded-lg group"
                    data-testid={`rapid-queue-item-${index}`}
                  >
                    <div className="flex items-center gap-3">
                      <Badge variant="outline" className="font-mono text-xs">
                        #{index + 1}
                      </Badge>
                      <code className="text-sm font-mono">{item.code}</code>
                    </div>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-6 w-6 opacity-0 group-hover:opacity-100"
                      onClick={() => removeFromQueue(item.code)}
                    >
                      <X className="w-3 h-3" />
                    </Button>
                  </div>
                ))}
              </div>
            </ScrollArea>
          </CardContent>
        </Card>
      )}
      
      {/* Instructions */}
      {!isActive && scanQueue.length === 0 && (
        <Alert>
          <Bluetooth className="h-4 w-4" />
          <AlertDescription>
            <strong>{t('rapid_scan.compatible', 'Compatible with:')}</strong>
            <ul className="list-disc list-inside mt-2 space-y-1 text-sm">
              <li>{t('rapid_scan.compat1', 'Bluetooth barcode scanners (HID mode)')}</li>
              <li>{t('rapid_scan.compat2', 'USB barcode scanners')}</li>
              <li>{t('rapid_scan.compat3', 'Scanners configured to add Enter after scan')}</li>
            </ul>
          </AlertDescription>
        </Alert>
      )}
    </div>
  );
}
